import { createSlice } from "@reduxjs/toolkit";

import activityTypes from "Constants/activityTypes";
import { ACTIVITY_CONTEXT, ACTIVITY_STATE } from "Constants/constants";
import { setDeep } from "Libs/objectAccess";
import { normalize } from "Libs/utils";

import {
  loadProjectActivities,
  loadEnvironmentActivities,
  loadIntegrationActivities,
  loadActivitySuccess,
  loadMoreEnvironmentActivities,
  loadMoreProjectActivities,
  loadMoreIntegrationActivities,
  loadEnvironmentStagedActivities,
  loadProjectActivity,
  restoreBackupByActivity,
  rescheduleActivity,
  loadProjectPendingScheduledActivities,
  loadEnvironmentPendingScheduledActivities
} from "./thunks";
import { UpdateSubscriptionActivity, ActivityState } from "./types";
import type { Activity, ActivityTypeFilters } from "Api/activity/types";

export const ActivityTabName = {
  Recent: "recent",
  All: "all"
};

export const getActivityTypes = (typeNames: string[]) =>
  typeNames.reduce<ActivityTypeFilters[]>((activityParams, type) => {
    const param = (
      activityTypes as Record<
        string,
        (typeof activityTypes)[keyof typeof activityTypes]
      >
    )[type];
    return param ? activityParams.concat(param.types) : activityParams;
  }, []);

const updateSubscriptionActivity = ({
  state,
  activityPath,
  activity,
  context
}: UpdateSubscriptionActivity) => {
  const filterNames = state?.filters?.[context] || [];
  const filters = getActivityTypes(filterNames);
  if (filters.length && !filters.includes(activity.type)) return state;
  setDeep(state, activityPath, activity);
  return state;
};

// Activities in `pendingScheduled` are only ever (re)fetched on mount or
// filter change, so a live update (this reducer case) is the only way to
// know a scheduled activity has since started running or completed.
// Without this, the stale "scheduled" copy keeps getting merged into the
// Pending section alongside its up-to-date self in Running/Recent.
const syncPendingScheduledActivity = ({
  state,
  activity,
  organizationDescriptionId,
  projectDescriptionId,
  environmentDescriptionId
}: {
  state: ActivityState;
  activity: Activity;
  organizationDescriptionId: string;
  projectDescriptionId: string;
  environmentDescriptionId?: string;
}) => {
  const isStillScheduled = activity.state === ACTIVITY_STATE.SCHEDULED;

  const byProjectActivities =
    state.pendingScheduled.byProject.data[organizationDescriptionId]?.[
      projectDescriptionId
    ];
  if (byProjectActivities?.[activity.id]) {
    if (isStillScheduled) {
      setDeep(
        state,
        [
          "pendingScheduled",
          "byProject",
          "data",
          organizationDescriptionId,
          projectDescriptionId,
          activity.id
        ],
        activity
      );
    } else {
      delete byProjectActivities[activity.id];
    }
  }

  const byEnvironmentActivities =
    environmentDescriptionId &&
    state.pendingScheduled.byEnvironment.data[organizationDescriptionId]?.[
      projectDescriptionId
    ]?.[environmentDescriptionId];
  if (byEnvironmentActivities?.[activity.id]) {
    if (isStillScheduled) {
      setDeep(
        state,
        [
          "pendingScheduled",
          "byEnvironment",
          "data",
          organizationDescriptionId,
          projectDescriptionId,
          environmentDescriptionId as string,
          activity.id
        ],
        activity
      );
    } else {
      delete byEnvironmentActivities[activity.id];
    }
  }

  return state;
};

const initialState: ActivityState = {
  filters: {
    tab: [ActivityTabName.All]
  },
  byProject: {
    loading: false,
    hasMore: false,
    loadingMore: false,
    data: {}
  },
  byEnvironment: {
    loading: false,
    hasMore: false,
    loadingMore: false,
    data: {}
  },
  byIntegration: {
    loading: false,
    hasMore: false,
    loadingMore: false,
    data: {}
  },
  staged: {
    loading: false,
    hasMore: false,
    loadingMore: false,
    data: {}
  },
  pendingScheduled: {
    byProject: { loading: false, data: {} },
    byEnvironment: { loading: false, data: {} }
  }
};

const activity = createSlice({
  name: "activity",
  initialState,
  reducers: {
    setActivitiesTab(state, action) {
      setDeep(state, ["filters", "tab"], action.payload);
      return state;
    },
    setActivityFilterNames(state, action) {
      setDeep(
        state,
        ["filters", action.payload?.context],
        action.payload?.filterNames
      );
      return state;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(restoreBackupByActivity.pending, state => {
        setDeep(state, ["byProject", "loading"], true);
      })
      .addCase(
        restoreBackupByActivity.fulfilled,
        (state, { payload, meta }) => {
          const { organizationDescriptionId, projectDescriptionId } = meta.arg;

          const currentActivities =
            state.byProject?.data?.[organizationDescriptionId]?.[
              projectDescriptionId
            ];
          const loadedActivities = normalize(payload || [], "id");
          const updatedActivities = Object.values({
            ...currentActivities,
            ...loadedActivities
          });

          setDeep(state, ["byProject", "loading"], false);
          setDeep(
            state,
            [
              "byProject",
              "data",
              organizationDescriptionId,
              projectDescriptionId
            ],
            normalize(updatedActivities, "id")
          );
        }
      )
      .addCase(restoreBackupByActivity.rejected, (state, { payload }) => {
        setDeep(state, ["byProject", "loading"], false);
        setDeep(state, ["error"], payload as Error);
      })
      .addCase(rescheduleActivity.fulfilled, (state, { payload, meta }) => {
        const { organizationId, projectId, activity } = meta.arg;

        if (
          state.byProject?.data?.[organizationId]?.[projectId]?.[activity.id]
        ) {
          setDeep(
            state,
            ["byProject", "data", organizationId, projectId, payload.id],
            payload
          );
        }

        const projectEnvs =
          state.byEnvironment?.data?.[organizationId]?.[projectId] ?? {};
        Object.keys(projectEnvs).forEach(environmentDescriptionId => {
          if (projectEnvs[environmentDescriptionId]?.[activity.id]) {
            setDeep(
              state,
              [
                "byEnvironment",
                "data",
                organizationId,
                projectId,
                environmentDescriptionId,
                activity.id
              ],
              payload
            );
          }
        });

        // Rescheduling an activity that's only surfaced via
        // pendingScheduled (i.e. absent from the paginated feed above,
        // which is the whole point of that branch) would otherwise never
        // get its updated scheduled_at reflected until a full refetch.
        syncPendingScheduledActivity({
          state,
          activity: payload,
          organizationDescriptionId: organizationId,
          projectDescriptionId: projectId
        });
        (payload.environments ?? []).forEach(environmentId => {
          syncPendingScheduledActivity({
            state,
            activity: payload,
            organizationDescriptionId: organizationId,
            projectDescriptionId: projectId,
            environmentDescriptionId: environmentId
          });
        });
      })
      .addCase(
        loadProjectPendingScheduledActivities.pending,
        (state, { meta }) => {
          const { organizationDescriptionId, projectDescriptionId } = meta.arg;
          setDeep(state, ["pendingScheduled", "byProject", "loading"], true);
          // A card and a full activity view for the same project can filter
          // by different types, and both write this same slot. Clear it
          // before the request lands so a stale, wrong-filter result can't
          // sit here if the request then fails (rejected only flips
          // loading back off, it doesn't know whose data this is).
          setDeep(
            state,
            [
              "pendingScheduled",
              "byProject",
              "data",
              organizationDescriptionId,
              projectDescriptionId
            ],
            {}
          );
        }
      )
      .addCase(
        loadProjectPendingScheduledActivities.fulfilled,
        (state, { payload }) => {
          const {
            organizationDescriptionId,
            projectDescriptionId,
            activities
          } = payload;
          setDeep(state, ["pendingScheduled", "byProject", "loading"], false);
          setDeep(
            state,
            [
              "pendingScheduled",
              "byProject",
              "data",
              organizationDescriptionId,
              projectDescriptionId
            ],
            normalize(activities || [], "id")
          );
        }
      )
      .addCase(
        loadProjectPendingScheduledActivities.rejected,
        (state, { payload }) => {
          setDeep(state, ["pendingScheduled", "byProject", "loading"], false);
          setDeep(state, ["error"], payload as Error);
        }
      )
      .addCase(
        loadEnvironmentPendingScheduledActivities.pending,
        (state, { meta }) => {
          const {
            organizationDescriptionId,
            projectDescriptionId,
            environmentDescriptionId
          } = meta.arg;
          setDeep(
            state,
            ["pendingScheduled", "byEnvironment", "loading"],
            true
          );
          // The Crons card and the environment's full activity view fetch
          // this same [org][project][env] slot with different type filters.
          // Clear it before the request lands so a stale, wrong-filter
          // result can't sit here if the request then fails (rejected only
          // flips loading back off, it doesn't know whose data this is).
          setDeep(
            state,
            [
              "pendingScheduled",
              "byEnvironment",
              "data",
              organizationDescriptionId,
              projectDescriptionId,
              environmentDescriptionId
            ],
            {}
          );
        }
      )
      .addCase(
        loadEnvironmentPendingScheduledActivities.fulfilled,
        (state, { payload }) => {
          const {
            organizationDescriptionId,
            projectDescriptionId,
            environmentDescriptionId,
            activities
          } = payload;
          setDeep(
            state,
            ["pendingScheduled", "byEnvironment", "loading"],
            false
          );
          setDeep(
            state,
            [
              "pendingScheduled",
              "byEnvironment",
              "data",
              organizationDescriptionId,
              projectDescriptionId,
              environmentDescriptionId
            ],
            normalize(activities || [], "id")
          );
        }
      )
      .addCase(
        loadEnvironmentPendingScheduledActivities.rejected,
        (state, { payload }) => {
          setDeep(
            state,
            ["pendingScheduled", "byEnvironment", "loading"],
            false
          );
          setDeep(state, ["error"], payload as Error);
        }
      )
      .addCase(loadProjectActivity.pending, state => {
        setDeep(state, ["byProject", "loading"], true);
      })
      .addCase(loadProjectActivity.fulfilled, (state, { payload, meta }) => {
        const { organizationDescriptionId, projectDescriptionId } = meta.arg;

        setDeep(state, ["byProject", "loading"], false);

        setDeep(
          state,
          [
            "byProject",
            "data",
            organizationDescriptionId,
            projectDescriptionId
          ],
          { [payload.id]: payload }
        );
      })
      .addCase(loadProjectActivity.rejected, (state, { payload }) => {
        setDeep(state, ["byProject", "loading"], false);
        setDeep(state, ["error"], payload as Error);
      })
      .addCase(loadProjectActivities.pending, state => {
        setDeep(state, ["byProject", "loading"], true);
      })
      .addCase(loadProjectActivities.fulfilled, (state, action) => {
        const {
          hasMore,
          organizationDescriptionId,
          projectDescriptionId,
          activities
        } = action.payload;
        setDeep(state, ["byProject", "loading"], false);
        setDeep(state, ["byProject", "hasMore"], hasMore);
        setDeep(
          state,
          [
            "byProject",
            "data",
            organizationDescriptionId,
            projectDescriptionId
          ],
          normalize(activities || [], "id")
        );
      })
      .addCase(loadProjectActivities.rejected, (state, { payload }) => {
        setDeep(state, ["byProject", "loading"], false);
        setDeep(state, ["byProject", "loadingMore"], false);
        setDeep(state, ["byEnvironment", "loading"], false);
        setDeep(state, ["byEnvironment", "loadingMore"], false);
        setDeep(state, ["byIntegration", "loading"], false);
        setDeep(state, ["byIntegration", "loadingMore"], false);
        setDeep(state, ["error"], payload as Error);
      })
      .addCase(loadEnvironmentActivities.pending, state => {
        setDeep(state, ["byEnvironment", "loading"], true);
      })
      .addCase(loadEnvironmentActivities.fulfilled, (state, action) => {
        const {
          hasMore,
          organizationDescriptionId,
          projectDescriptionId,
          environmentDescriptionId,
          activities
        } = action.payload;

        setDeep(state, ["byEnvironment", "loading"], false);
        setDeep(state, ["byEnvironment", "hasMore"], hasMore);
        setDeep(
          state,
          [
            "byEnvironment",
            "data",
            organizationDescriptionId,
            projectDescriptionId,
            environmentDescriptionId
          ],
          normalize(activities || [], "id")
        );
      })
      .addCase(loadEnvironmentActivities.rejected, (state, { payload }) => {
        setDeep(state, ["byProject", "loading"], false);
        setDeep(state, ["byProject", "loadingMore"], false);
        setDeep(state, ["byEnvironment", "loading"], false);
        setDeep(state, ["byEnvironment", "loadingMore"], false);
        setDeep(state, ["byIntegration", "loading"], false);
        setDeep(state, ["byIntegration", "loadingMore"], false);
        setDeep(state, ["error"], payload as Error);
      })
      .addCase(loadEnvironmentStagedActivities.pending, state => {
        setDeep(state, ["staged", "loading"], true);
      })
      .addCase(loadEnvironmentStagedActivities.fulfilled, (state, action) => {
        const {
          hasMore,
          organizationDescriptionId,
          projectDescriptionId,
          environmentDescriptionId,
          activities
        } = action.payload;
        setDeep(state, ["staged", "loading"], false);
        setDeep(state, ["staged", "hasMore"], hasMore);
        setDeep(
          state,
          [
            "staged",
            "data",
            organizationDescriptionId,
            projectDescriptionId,
            environmentDescriptionId
          ],
          normalize(activities || [], "id")
        );
      })
      .addCase(
        loadEnvironmentStagedActivities.rejected,
        (state, { payload }) => {
          setDeep(state, ["staged", "loading"], false);
          setDeep(state, ["staged", "loadingMore"], false);
          setDeep(state, ["error"], payload as Error);
        }
      )

      .addCase(loadIntegrationActivities.pending, state => {
        setDeep(state, ["byIntegration", "loading"], true);
      })
      .addCase(loadIntegrationActivities.fulfilled, (state, action) => {
        const {
          hasMore,
          organizationDescriptionId,
          projectDescriptionId,
          integrationDescriptionId,
          activities
        } = action.payload;
        setDeep(state, ["byIntegration", "loading"], false);
        setDeep(state, ["byIntegration", "hasMore"], hasMore);
        setDeep(
          state,
          [
            "byIntegration",
            "data",
            organizationDescriptionId,
            projectDescriptionId,
            integrationDescriptionId
          ],
          normalize(activities || [], "id")
        );
      })
      .addCase(loadIntegrationActivities.rejected, (state, { payload }) => {
        setDeep(state, ["byProject", "loading"], false);
        setDeep(state, ["byProject", "loadingMore"], false);
        setDeep(state, ["byEnvironment", "loading"], false);
        setDeep(state, ["byEnvironment", "loadingMore"], false);
        setDeep(state, ["byIntegration", "loading"], false);
        setDeep(state, ["byIntegration", "loadingMore"], false);
        setDeep(state, ["error"], payload as Error);
      })
      .addCase(loadMoreEnvironmentActivities.pending, state => {
        setDeep(state, ["byEnvironment", "loadingMore"], true);
      })
      .addCase(loadMoreEnvironmentActivities.fulfilled, (state, action) => {
        const {
          hasMore,
          organizationDescriptionId,
          projectDescriptionId,
          environmentDescriptionId,
          activities
        } = action.payload;
        const currentActivities =
          state.byEnvironment?.data?.[organizationDescriptionId]?.[
            projectDescriptionId
          ]?.[environmentDescriptionId];
        const loadedActivities = normalize(activities, "id");
        const updatedActivities = Object.values({
          ...currentActivities,
          ...loadedActivities
        });
        setDeep(state, ["byEnvironment", "loadingMore"], false);
        setDeep(state, ["byEnvironment", "hasMore"], hasMore);
        setDeep(
          state,
          [
            "byEnvironment",
            "data",
            organizationDescriptionId,
            projectDescriptionId,
            environmentDescriptionId
          ],
          normalize(updatedActivities, "id")
        );
      })
      .addCase(loadMoreEnvironmentActivities.rejected, (state, { payload }) => {
        setDeep(state, ["byProject", "loading"], false);
        setDeep(state, ["byProject", "loadingMore"], false);
        setDeep(state, ["byEnvironment", "loading"], false);
        setDeep(state, ["byEnvironment", "loadingMore"], false);
        setDeep(state, ["byIntegration", "loading"], false);
        setDeep(state, ["byIntegration", "loadingMore"], false);
        setDeep(state, ["error"], payload as Error);
      })
      .addCase(loadMoreProjectActivities.pending, state => {
        setDeep(state, ["byProject", "loadingMore"], true);
      })
      .addCase(loadMoreProjectActivities.fulfilled, (state, action) => {
        const {
          hasMore,
          organizationDescriptionId,
          projectDescriptionId,
          activities
        } = action.payload;

        const currentActivities =
          state.byProject?.data?.[organizationDescriptionId]?.[
            projectDescriptionId
          ];
        const loadedActivities = normalize(activities, "id");
        const updatedActivities = Object.values({
          ...currentActivities,
          ...loadedActivities
        });
        setDeep(state, ["byProject", "loadingMore"], false);
        setDeep(state, ["byProject", "hasMore"], hasMore);
        setDeep(
          state,
          [
            "byProject",
            "data",
            organizationDescriptionId,
            projectDescriptionId
          ],
          normalize(updatedActivities, "id")
        );
      })
      .addCase(loadMoreProjectActivities.rejected, (state, { payload }) => {
        setDeep(state, ["byProject", "loading"], false);
        setDeep(state, ["byProject", "loadingMore"], false);
        setDeep(state, ["byEnvironment", "loading"], false);
        setDeep(state, ["byEnvironment", "loadingMore"], false);
        setDeep(state, ["byIntegration", "loading"], false);
        setDeep(state, ["byIntegration", "loadingMore"], false);
        setDeep(state, ["error"], payload as Error);
      })
      .addCase(loadMoreIntegrationActivities.pending, state => {
        setDeep(state, ["byIntegration", "loadingMore"], true);
      })
      .addCase(loadMoreIntegrationActivities.fulfilled, (state, action) => {
        const {
          hasMore,
          organizationDescriptionId,
          projectDescriptionId,
          integrationDescriptionId,
          activities
        } = action.payload;

        const currentActivities =
          state.byIntegration?.data?.[organizationDescriptionId]?.[
            projectDescriptionId
          ]?.[integrationDescriptionId] || {};
        const loadedActivities = normalize(activities, "id");
        const updatedActivities = Object.values({
          ...currentActivities,
          ...loadedActivities
        });
        setDeep(state, ["byIntegration", "loadingMore"], false);
        setDeep(state, ["byIntegration", "hasMore"], hasMore);
        setDeep(
          state,
          [
            "byIntegration",
            "data",
            organizationDescriptionId,
            projectDescriptionId,
            integrationDescriptionId
          ],
          normalize(updatedActivities, "id")
        );
      })
      .addCase(loadMoreIntegrationActivities.rejected, (state, { payload }) => {
        setDeep(state, ["byProject", "loading"], false);
        setDeep(state, ["byProject", "loadingMore"], false);
        setDeep(state, ["byEnvironment", "loading"], false);
        setDeep(state, ["byEnvironment", "loadingMore"], false);
        setDeep(state, ["byIntegration", "loading"], false);
        setDeep(state, ["byIntegration", "loadingMore"], false);
        setDeep(state, ["error"], payload as Error);
      })
      .addCase(loadActivitySuccess.pending, () => {})
      .addCase(loadActivitySuccess.rejected, () => {})
      .addCase(loadActivitySuccess.fulfilled, (state, { payload = [] }) => {
        payload.forEach(payload => {
          const {
            organizationDescriptionId,
            projectDescriptionId,
            environmentDescriptionId,
            integrationDescriptionId,
            activity
          } = payload;

          const byProject = updateSubscriptionActivity({
            state: state as ActivityState,
            activityPath: [
              "byProject",
              "data",
              organizationDescriptionId,
              projectDescriptionId,
              activity.id
            ],
            activity: activity,
            context: ACTIVITY_CONTEXT.Project
          });

          const byEnvironment = updateSubscriptionActivity({
            state: byProject,
            activityPath: [
              "byEnvironment",
              "data",
              organizationDescriptionId,
              projectDescriptionId,
              environmentDescriptionId,
              activity.id
            ],
            activity: activity,
            context: ACTIVITY_CONTEXT.Environment
          });

          syncPendingScheduledActivity({
            state: byEnvironment,
            activity,
            organizationDescriptionId,
            projectDescriptionId,
            environmentDescriptionId
          });

          if (activity.state === "staged") {
            state = updateSubscriptionActivity({
              state: byEnvironment,
              activityPath: [
                "staged",
                "data",
                organizationDescriptionId,
                projectDescriptionId,
                environmentDescriptionId,
                activity.id
              ],
              activity: activity,
              context: ACTIVITY_CONTEXT.Environment
            });
          }
          const stagedActivities =
            byEnvironment.staged.data?.[organizationDescriptionId]?.[
              projectDescriptionId
            ]?.[environmentDescriptionId];

          if (
            activity.state === "complete" &&
            Object.keys(stagedActivities || {}).find(id => id === activity.id)
          ) {
            delete stagedActivities?.[activity.id];
            setDeep(
              state,
              [
                "staged",
                "data",
                organizationDescriptionId,
                projectDescriptionId,
                environmentDescriptionId
              ],
              stagedActivities
            );
          }

          if (integrationDescriptionId) {
            state = updateSubscriptionActivity({
              state: byEnvironment,
              activityPath: [
                "byIntegration",
                "data",
                organizationDescriptionId,
                projectDescriptionId,
                integrationDescriptionId,
                activity.id
              ],
              activity: activity,
              context: ACTIVITY_CONTEXT.Integration
            });
          }
        });
        setDeep(state, ["byProject", "loading"], false);
        setDeep(state, ["byEnvironment", "loading"], false);
        setDeep(state, ["byIntegration", "loading"], false);
        setDeep(state, ["staged", "loading"], false);
      });
  }
});

export const { setActivitiesTab, setActivityFilterNames } = activity.actions;
export default activity.reducer;
